import { inject, observer } from 'mobx-react';
import * as React from 'react';

import { ILocalization } from 'bernie-l10n';
import { ViewLarge, Viewport, ViewSmall } from 'bernie-view';

import { Experiment, ExperimentControl, ExperimentVariant } from '@shared-ui/experiment-context';

import { SearchCriteriaModel } from 'stores/models';
import { SearchResultsStore } from 'stores/search-results-store';

import { LodgingPwaPropertySearch, SearchDialogMessage, SearchLinkMessage } from '__generated__/api/types';
import { LoyaltyMessagingModel } from 'models';
import { SortAndFilterModel } from 'components/common/search-form/fields/filter-types';

import { classNames } from 'uitk-react-utils';
import { UitkLayoutGrid, UitkLayoutGridItem } from 'uitk-react-layout-grid';
import { UitkText } from 'uitk-react-text';

import { HeaderMessageLink } from './header-message-link';
import { SearchForm } from 'components/common/search-form/index';
import { SWPToggle } from 'components/common/swp-toggle';

const defaultClasses = 'pwa-theme--grey-700 all-b-padding-one';

export enum ResultsHeaderMessageAlignment {
  Left = 'uitk-type-left',
  Center = 'uitk-type-center'
}

export interface ResultsHeaderProps {
  messageAlignment: ResultsHeaderMessageAlignment;
  headerSectionPaddingClasses?: string;
  resultsSummary?: (LodgingPwaPropertySearch.ResultsSummary & SearchLinkMessage.Fragment & SearchDialogMessage.Fragment)[];
  isLoading: boolean;
  l10n: ILocalization;
  loyaltyMessagingModel?: LoyaltyMessagingModel;
  searchCriteria: SearchCriteriaModel;
  classes?: string;
  searchResults?: SearchResultsStore;
}

export const ResultsHeader: React.FunctionComponent<ResultsHeaderProps> = inject('searchResults')(
  observer(props => {
    const {
      headerSectionPaddingClasses,
      messageAlignment,
      resultsSummary,
      isLoading,
      l10n,
      loyaltyMessagingModel,
      searchCriteria,
      searchResults
    } = props;
    const multiItem = searchCriteria && searchCriteria.misId;
    const lob = multiItem ? 'multiItem' : 'hotels';
    const classes = props.classes || defaultClasses;
    const searchResultsCriteria: SearchCriteriaModel = searchResults && searchResults.searchCriteria;
    const sortAndFilterOptions: SortAndFilterModel = searchResults && searchResults.sortAndFilter && searchResults.sortAndFilter.options;
    const filters = searchResults && searchResults.searchAndFilterDisplayOptions && searchResults.searchAndFilterDisplayOptions.filters;
    const headerClasses: string = classNames(`tools__summary ${headerSectionPaddingClasses}`);

    const Loading = () => (
      <>
        {isLoading && (
          <p className={`${classes} ${messageAlignment} all-b-padding-two`}>
            {l10n.formatText(`${lob}.searchResults.loadingMessage.text`)}
          </p>
        )}
      </>
    );

    return (
      <header className={headerClasses}>
        <Viewport>
          <ViewSmall>
            <>
              <Loading />
              {!isLoading && (
                <>
                  <SWPToggle model={loyaltyMessagingModel} searchCriteria={searchCriteria} />
                  <HeaderMessages messageAlignment={messageAlignment} resultsSummary={resultsSummary} classes={classes} />
                </>
              )}
            </>
          </ViewSmall>
          <ViewLarge>
            <UitkLayoutGrid columns={8}>
              <UitkLayoutGridItem colSpan={5}>
                <div>
                  <Loading />
                  {!isLoading && (
                    <>
                      <SWPToggle model={loyaltyMessagingModel} searchCriteria={searchCriteria} />
                      <HeaderMessages messageAlignment={messageAlignment} resultsSummary={resultsSummary} classes={classes} />
                    </>
                  )}
                </div>
              </UitkLayoutGridItem>
              <UitkLayoutGridItem colSpan={3}>
                <div>
                  <Experiment name="PWA_SORT_TNL_THROTTLE">
                    <ExperimentControl>
                      <Experiment name="PWA_Redesign_Sort_and_Filter_Phase_1">
                        <ExperimentControl />
                        <ExperimentVariant bucket={1}>
                          <SearchForm
                            model={searchResultsCriteria}
                            sortAndFilterOptions={sortAndFilterOptions}
                            sortAndFilterDefaults={searchResults && searchResults.sortAndFilter && searchResults.sortAndFilter.defaults}
                            provideDefaultSubmitButton={false}
                            displayFields={['sort']}
                            ignoreFields={filters && filters.ignoreFields}
                            autoSubmit={true}
                            searchable={true}
                          />
                        </ExperimentVariant>
                      </Experiment>
                    </ExperimentControl>
                    <ExperimentVariant bucket={1} />
                  </Experiment>
                </div>
              </UitkLayoutGridItem>
            </UitkLayoutGrid>
          </ViewLarge>
        </Viewport>
      </header>
    );
  })
);
ResultsHeader.displayName = 'ResultsHeader';

export interface HeaderMessagesProps {
  messageAlignment: ResultsHeaderMessageAlignment;
  resultsSummary?: (LodgingPwaPropertySearch.ResultsSummary & SearchLinkMessage.Fragment & SearchDialogMessage.Fragment)[];
  classes?: string;
}

export const HeaderMessages: React.FunctionComponent<HeaderMessagesProps> = props => {
  const { messageAlignment, resultsSummary } = props;
  const classes = props.classes || defaultClasses;

  if (!resultsSummary || resultsSummary.length === 0) {
    return null;
  }

  return (
    <div className="uitk-flex uitk-flex-column s-t-padding-one" data-stid="results-header-messages">
      {resultsSummary.map((message, index) => {
        const { link, value, dialogTrigger } = message;

        if (!value) {
          return null;
        }

        return (
          <div className={`${classes} ${messageAlignment}`} data-stid="results-header-message" key={`header-message-${index}`}>
            {link || dialogTrigger ? (
              <HeaderMessageLink {...message} />
            ) : (
              <UitkText size={200} weight="regular">
                {value}
              </UitkText>
            )}
          </div>
        );
      })}
    </div>
  );
};
HeaderMessages.displayName = 'HeaderMessages';
